import React, { useCallback, useEffect, useState } from "react";
import {
  AppDatePicker,
  AppFileUploader,
  AppMultiSelect,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from "@/layout/AppComponents";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Formik } from "formik";
import Moment from "moment";
import * as Yup from "yup";
import ReactDOMServer from "react-dom/server";
import { axiosPath } from "../../../../../../../routing/helpers/router";
import Translator from "../../../../../../components/helpers/Translator";
import { toFormData } from "../../../../../../../minions/forms";
import errorHandler from "@/layout/components/error/ErrorHandler";
import SpinnerLoader from "../../../../../../../layout/components/helpers/SpinnerLoader";
import colors from "../../../../../../../constants/colors";
import AppTextEditor from "../../../../../../components/form/Texts/AppTextEditor";
import useIsMounted from "../../../../../../components/hooks/useIsMounted";

export default function CorrectiveActionsTaskUpdateAdd(props) {
  const { taskID, modulePermissions, onCreate, onCancel } = props;
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const level1 = useSelector((state) => state.currentPage?.levels?.level1);
  const [isLoading, setIsLoading] = useState(true);
  const [typeOptions, setTypeOptions] = useState();
  const [responsibleOptions, setResponsibleOptions] = useState();

  // < GET LISTS
  const getEditLists = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_correctiveActions_taskUpdate_getEditLists"
          ),
          {
            moduleBridgeHex,
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setTypeOptions(r.data.typeOptions);
            setResponsibleOptions(r.data.responsibleOptions);
            setIsLoading(false);
          }
        }),
    [moduleBridgeHex]
  );

  useEffect(() => {
    getEditLists();
  }, []);
  // > GET LISTS

  // < ADD
  const handleAdd = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_taskUpdate_add"),
          toFormData({
            ...payload,
            ...{
              moduleBridgeHex,
              taskID,
            },
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          if (isMounted.current) onCreate();
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID]
  );
  // > ADD

  const validationSchema = Yup.object().shape({
    dateStamp: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.date" />)
    ),
    type: Yup.mixed().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.origin" />)
    ),
    description: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
  });

  if (isLoading)
    return (
      <AppNoInfo color={colors.primaryLight}>
        <SpinnerLoader size={30} />
      </AppNoInfo>
    );

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          dateStamp: Moment().format("DD/MM/YYYY"),
          limitDate: "",
          type: null,
          files: [],
          employees: [],
          description: "",
        }}
        onSubmit={handleAdd}
      >
        {({ setFieldValue, handleSubmit, values, isSubmitting, errors }) => (
          <form onSubmit={handleSubmit}>
            <div className="row-no-margin">
              <div className="row">
                <div className="col-lg-12">
                  <h3>
                    {ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="new.action.code"
                        variables={{ code: level1[taskID]?.code }}
                      />
                    )}
                  </h3>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <AppDatePicker
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="creation.date"
                      />
                    )}
                    value={values.dateStamp}
                    onChange={(opt) => setFieldValue("dateStamp", opt)}
                    placeholder="dd/mm/yyyy"
                    error={errors.dateStamp}
                    showMonthDropdown
                    showYearDropdown
                    required
                  />
                </div>
                {modulePermissions?.correctiveActions
                  ?.taskUpdateClosedSystem && (
                  <div className="col-lg-6">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="module.lc.limit.date"
                        />
                      )}
                      value={values.limitDate}
                      onChange={(opt) => setFieldValue("limitDate", opt)}
                      placeholder="dd/mm/yyyy"
                      error={errors.limitDate}
                      showMonthDropdown
                      showYearDropdown
                      isClearable
                    />
                  </div>
                )}
                <div className="col-lg-6">
                  {typeOptions && (
                    <AppSingleSelect
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.task-type-2"
                        />
                      )}
                      icon={<AppRemixIcon icon="focus" />}
                      value={values.type}
                      options={typeOptions}
                      onChange={(v) => setFieldValue("type", v)}
                      error={errors.type}
                      required
                    />
                  )}
                </div>
                {modulePermissions?.correctiveActions
                  ?.taskUpdateResponsibles && (
                  <div className="col-lg-6">
                    <AppMultiSelect
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="responsibles"
                        />
                      )}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.filters.employee-select"
                        />
                      )}
                      icon={<AppRemixIcon icon="user" />}
                      value={values.employees}
                      options={responsibleOptions}
                      onChange={(v) => setFieldValue("employees", v)}
                      isLoading={!responsibleOptions}
                      hasSearchBox
                      hasSelectOptions
                    />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-legCompliance" name="comment" />
                    )}
                    height="195px"
                    isReadOnly
                    value={
                      <AppTextEditor
                        value={values.description}
                        onChange={(opt) => setFieldValue("description", opt)}
                        toolbar={{
                          toolbar: [["bold", "italic"]],
                        }}
                        height={138}
                      />
                    }
                    error={errors.description}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <AppFileUploader
                    placeholder={
                      values.files.length === 0 ? (
                        <Translator file="general" name="select.files" />
                      ) : (
                        <Translator
                          file="admin"
                          name="general.elements-selected-elements"
                          variables={{ elements: values.files.length }}
                        />
                      )
                    }
                    onChange={(opt) => setFieldValue("files", opt)}
                    btnText={<Translator file="general" name="search..." />}
                    isMulti
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="float-right">
                    <button
                      type="button"
                      className="btn btn-outline-light btn-sm mr-2"
                      onClick={onCancel}
                      disabled={isSubmitting}
                    >
                      <Translator file="user" name="user.cancel" />
                    </button>
                    <button type="submit" className="btn btn-success btn-sm">
                      <Translator
                        file="user"
                        name="user.obligation.evidence.create"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
}
